import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Plane, ArrowRight, Clock, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { inr, prettyDate } from "@/lib/format";
import Shell from "@/components/Shell";
import SearchWidget from "@/components/SearchWidget";

export default function FlightResults() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const from = params.get("from"), to = params.get("to"), date = params.get("date");
  const pax = Number(params.get("pax") || 1);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!from || !to || !date) { setLoading(false); return; }
    setLoading(true);
    api.get(`/flights/search?from=${from}&to=${to}&date=${date}&pax=${pax}`)
      .then((r) => setResults(r.data.results)).finally(() => setLoading(false));
  }, [from, to, date, pax]);

  const select = (f) => navigate("/checkout", { state: {
    type: "flight", item_id: f.id, title: `${f.from_name} → ${f.to_name}`, image: null,
    amount: f.price * pax, currency: "inr",
    details: { from: f.from, to: f.to, from_name: f.from_name, to_name: f.to_name, date: f.date, airline: f.airline, flight_no: f.flight_no, depart: f.depart, arrive: f.arrive, price: f.price, pax },
    summary: [{ k: "Airline", v: `${f.airline} ${f.flight_no}` }, { k: "Date", v: prettyDate(f.date) }, { k: "Depart", v: f.depart }, { k: "Travellers", v: pax }, { k: "Fare/person", v: inr(f.price) }],
    icon: "flight",
  }});

  return (
    <Shell>
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-8">
        <SearchWidget initialTab="flights" />
      </section>
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
        <h1 className="font-display text-3xl text-foreground mb-1">Flights {from} → {to}</h1>
        <p className="text-muted-foreground text-sm mb-6">{prettyDate(date)} · {pax} traveller(s)</p>
        {loading ? (
          <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-24 rounded-xl bg-secondary/60 animate-pulse" />)}</div>
        ) : results.length === 0 ? (
          <p className="text-muted-foreground py-16 text-center">No flights found. Try different cities.</p>
        ) : (
          <div className="space-y-3" data-testid="flight-results">
            {results.map((f, i) => (
              <div key={f.id} className="rounded-xl border border-primary/10 bg-card p-5 flex flex-wrap items-center gap-6 justify-between" data-testid={`flight-${i}`}>
                <div className="flex items-center gap-4">
                  <span className="grid place-items-center w-11 h-11 rounded-lg bg-secondary text-accent"><Plane size={20} /></span>
                  <div>
                    <p className="font-semibold text-foreground">{f.airline}</p>
                    <p className="text-xs text-muted-foreground">{f.flight_no} · {f.cabin}</p>
                  </div>
                </div>
                <div className="flex items-center gap-5 text-center">
                  <div><p className="font-display text-2xl text-foreground">{f.depart}</p><p className="text-xs text-muted-foreground">{f.from}</p></div>
                  <div className="text-muted-foreground text-xs"><Clock size={13} className="mx-auto mb-1" />{f.duration}<br />{f.stops === 0 ? "Non-stop" : `${f.stops} stop`}</div>
                  <div><p className="font-display text-2xl text-foreground">{f.arrive}</p><p className="text-xs text-muted-foreground">{f.to}</p></div>
                </div>
                <div className="text-right">
                  <p className="font-display text-2xl text-foreground">{inr(f.price)}</p>
                  <button onClick={() => select(f)} data-testid={`select-flight-${i}`} className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-accent text-accent-foreground px-5 py-2 text-sm font-semibold hover:-translate-y-px transition-transform">Select <ArrowRight size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </Shell>
  );
}
